export type GroupWidths = { expanded: number; collapsed: number }

const SAFETY_MARGIN = 1

const isMeasured = (widths: readonly GroupWidths[]): boolean =>
    widths.length > 0 && widths.every(width => width.expanded > 0 && width.collapsed > 0)

/**
 * Which groups of a FilterSwitch row collapse for the row to fit (aos-ds
 * `collapsePlan`, branch AOS-1388): as few as possible, widest saving first,
 * never one whose trigger is no narrower than its pills, and none at all until
 * every width is measured. One pixel stays unclaimed, because landing on the
 * boundary wraps the row.
 */
export const collapsePlan = (
    widths: readonly GroupWidths[],
    available: number,
    gap: number,
): boolean[] => {
    const plan = widths.map(() => false)
    if (available <= 0 || !isMeasured(widths)) {
        return plan
    }

    const room = available - SAFETY_MARGIN
    const gaps = gap * (widths.length - 1)
    const used = () =>
        widths.reduce(
            (total, width, index) => total + (plan[index] ? width.collapsed : width.expanded),
            gaps,
        )

    if (used() <= room) {
        return plan
    }

    const bySaving = widths
        .map((width, index) => ({ index, saving: width.expanded - width.collapsed }))
        .filter(candidate => candidate.saving > 0)
        .sort((a, b) => b.saving - a.saving || a.index - b.index)

    for (const candidate of bySaving) {
        plan[candidate.index] = true
        if (used() <= room) {
            break
        }
    }

    return plan
}

/**
 * True when collapsing has nothing left to give: every group that can collapse
 * has. The row wraps only then. False until every width is measured, so a first
 * paint never wraps.
 */
export const collapseExhausted = (
    widths: readonly GroupWidths[],
    plan: readonly boolean[],
): boolean =>
    isMeasured(widths) &&
    widths.every((width, index) => plan[index] || width.expanded <= width.collapsed)
